import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  FormHelperText,
  Snackbar,
  Alert,
  Modal,
} from "@mui/material";
import emailjs from "@emailjs/browser";
import Lottie from "lottie-react";
import { motion } from "framer-motion";
import axios from "axios";
import CryptoJS from "crypto-js";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    message: "",
    team: "",
    role: "",
  });

  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [animationData, setAnimationData] = useState(null);
  const [vector, setVector] = useState(null);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const firstInputRef = useRef(null);
  const serverURL = process.env.REACT_APP_URL;
  // const secretKey = "otpverify"; // Keep this secure and consistent with backend

  // Fetch animation JSON from public folder
  useEffect(() => {
    fetch("/assests/lottie/animation.json")
      .then((res) => res.json())
      .then((data) => setAnimationData(data))
      .catch((err) => console.error("Error loading animation:", err));

    fetch("/assests/lottie/vector.json")
      .then((res) => res.json())
      .then((data) => setVector(data))
      .catch((err) => console.error("Error loading animation:", err));
  }, []);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle phone number input (numeric only)
    if (name === "phoneNumber") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length > 10) return; // Limit to 10 digits
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for the current field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return; // Allow only numbers
    let newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Move to next input if filled
    if (value && index < 3) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  };
  const sendOtpToPhone = async (formData) => {
    try {
      const response = await axios.post(`${serverURL}/api/contact/sentOTP`, {
        phoneNumber: formData.phoneNumber,
        name: formData.name,
        email: formData.email,
        message: formData.message,
        role: formData.role,
        team: formData.team,
      });

      if (response.data.success) {
        setOtpModalOpen(true);
        // alert("OTP has been sent to your phone.");
      } else {
        console.error("Failed to send OTP:", response.data.message);
        setSnackbar({
          open: true,
          message: "Failed to send OTP",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || "Failed to send OTP",
        severity: "error",
      });
    }
  };

  // const handleVerifyOtp = () => {
  //   if (otp.join("") === generatedOtp) {
  //     setOtpError("");
  //     setOtpModalOpen(false);

  //     setOpen(true);

  //     // sendEmail();
  //     setOtp(["", "", "", ""]);
  //   } else {
  //     setOtpError("Invalid OTP. Please try again.");
  //     setOtp(["", "", "", ""]);
  //   }
  // };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    if (!enteredOtp) {
      setOtpError("Please enter OTP.");
      return;
    }

    if (enteredOtp.length !== 4) {
      setOtpError("Please enter complete OTP.");
      return;
    }

    try {
      setOtpLoading(true);
      const response = await axios.post(`${serverURL}/api/contact/verifyOTP`, {
        email: formData.email,
        otp: enteredOtp,
        phoneNumber: formData.phoneNumber,
      });

      if (response.data.success) {
        setOtpError("");
        setOtpModalOpen(false);
        setOpen(true);
        setOtp(["", "", "", ""]);

        // Reset form data
        setFormData({
          name: "",
          email: "",
          phoneNumber: "",
          message: "",
          team: "",
          role: "",
        });
      } else {
        setOtpError(response.data.error || "Invalid OTP. Please try again.");
        setOtp(["", "", "", ""]);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError(
        error.response?.data?.error || "Failed to verify OTP. Please try again."
      );
      setOtp(["", "", "", ""]);
    } finally {
      setOtpLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required.";
    } else if (formData.phoneNumber.length !== 10) {
      newErrors.phoneNumber = "Phone number must be 10 digits.";
    }
    if (!formData.role.trim()) {
      newErrors.role = "Role is required.";
    }
    if (!formData.team.trim()) {
      newErrors.team = "Team is required.";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Message is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // const sendEmail = () => {
  //   const templateParams = {
  //     from_name: formData.name,
  //     from_email: formData.email,
  //     message: formData.message,
  //     role: formData.role,
  //     team: formData.team,
  //     from_phoneNumber: formData.phoneNumber,
  //   };

  //   emailjs
  //     .send(
  //       "service_9ar63ep",
  //       "template_y6d66fv",
  //       templateParams,
  //       "1B_qD_r8ss_8Uj5x9"
  //     )
  //     .then(
  //       () => {
  //         setFormData({
  //           name: "",
  //           email: "",
  //           phoneNumber: "",
  //           message: "",
  //           role: "",
  //           team: "",
  //         });
  //         // setSnackbar({
  //         //   open: true,
  //         //   message: "Form submitted successfully!",
  //         //   severity: "success",
  //         // });
  //       },
  //       (error) => {
  //         console.log(error);
  //       }
  //     );
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true); // Disable button
      // const otpCode = generateOtp();
      await sendOtpToPhone(formData);
      setLoading(false); // Enable button after API response
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1000px",
        margin: "0 auto",
        padding: "2rem",
        textAlign: "center",
        marginTop: "8rem",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          marginBottom: "1rem",
          fontWeight: "bold",
          textTransform: "uppercase",
          color: "#aaa",
          fontSize: "12px",
        }}
      >
        Get Started
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          marginBottom: "4rem",
          fontSize: "30px",
        }}
      >
        Book a call now. We're here to assist you.
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Name Field */}
          <Grid item xs={12} sm={6} lg={4}>
            <Box sx={{ textAlign: "left" }}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                  color: errors.name ? "red" : "",
                }}
              >
                Name*
              </label>
              <TextField
                fullWidth
                name="name"
                value={formData.name}
                onChange={handleChange}
                variant="outlined"
                error={!!errors.name}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "12px",
                  },
                }}
              />
              {errors.name && (
                <FormHelperText sx={{ color: "var(--error-color)" }}>
                  {errors.name}
                </FormHelperText>
              )}
            </Box>
          </Grid>

          {/* Email Field */}
          <Grid item xs={12} sm={6} lg={4}>
            <Box sx={{ textAlign: "left" }}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                  color: errors.email ? "red" : "",
                }}
              >
                Email*
              </label>
              <TextField
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                variant="outlined"
                error={!!errors.email}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "12px",
                  },
                }}
              />
              {errors.email && (
                <FormHelperText sx={{ color: "var(--error-color)" }}>
                  {errors.email}
                </FormHelperText>
              )}
            </Box>
          </Grid>

          {/* Phone Number Field */}
          <Grid item xs={12} sm={6} lg={4}>
            <Box sx={{ textAlign: "left" }}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                  color: errors.phoneNumber ? "red" : "",
                }}
              >
                Phone Number*
              </label>
              <TextField
                fullWidth
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                type="phoneNumber"
                variant="outlined"
                error={!!errors.phoneNumber}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "12px",
                  },
                }}
              />
              {errors.phoneNumber && (
                <FormHelperText sx={{ color: "var(--error-color)" }}>
                  {errors.phoneNumber}
                </FormHelperText>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Box sx={{ textAlign: "left" }}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                  color: errors.role ? "red" : "",
                }}
              >
                Your Role*
              </label>
              <TextField
                select
                fullWidth
                name="role"
                value={formData.role}
                onChange={handleChange}
                variant="outlined"
                error={!!errors.role}
                SelectProps={{ native: true }}
                sx={{ "& .MuiInputBase-root": { borderRadius: "12px" } }}
              >
                <option value="">Select Role</option>
                <option value="Brand">Brand</option>
                <option value="Vendor Partner">Vendor Partner</option>
                <option value="Installation Partner">
                  Installation Partner
                </option>
              </TextField>
              {errors.role && (
                <FormHelperText sx={{ color: "var(--error-color)" }}>
                  {errors.role}
                </FormHelperText>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Box sx={{ textAlign: "left" }}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                  color: errors.team ? "red" : "",
                }}
              >
                Which team do you want to talk to?*
              </label>
              <TextField
                select
                fullWidth
                name="team"
                value={formData.team}
                onChange={handleChange}
                variant="outlined"
                error={!!errors.team}
                SelectProps={{ native: true }}
                sx={{ "& .MuiInputBase-root": { borderRadius: "12px" } }}
              >
                <option value="">Select Department</option>
                <option value="Sales">Sales</option>
                <option value="Procurement">Procurement</option>
                <option value="Vendor Onboarding">Vendor Onboarding</option>
                <option value="Installation Partner Onboarding">
                  Installation Partner Onboarding
                </option>
                <option value="After Sales">After Sales</option>
                <option value="Hiring">Hiring</option>
                <option value="Affiliation and partnerships">
                  Affiliation and partnerships
                </option>
                <option value="Others">Others</option>
              </TextField>
              {errors.team && (
                <FormHelperText sx={{ color: "var(--error-color)" }}>
                  {errors.team}
                </FormHelperText>
              )}
            </Box>
          </Grid>

          {/* Message Field */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: "left" }}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                  color: errors.message ? "red" : "",
                }}
              >
                Message*
              </label>
              <TextField
                fullWidth
                name="message"
                value={formData.message}
                onChange={handleChange}
                variant="outlined"
                multiline
                error={!!errors.message}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "12px",
                  },
                }}
              />
              {errors.message && (
                <FormHelperText sx={{ color: "var(--error-color)" }}>
                  {errors.message}
                </FormHelperText>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Submit Button */}
        <Box sx={{ textAlign: "left", marginTop: "2rem" }}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: loading ? "#ccc" : "#007bff",
              color: "#fff",
              borderRadius: "10px",
              padding: "0.5rem 1rem",
              fontWeight: "bold",
              textTransform: "none",
              "&:hover": { backgroundColor: loading ? "#ccc" : "#0056b3" },
            }}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit ➞"}
          </Button>
        </Box>
      </form>
      <Modal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setOtp(["", "", "", ""]);
          setOtpError("");
        }}
        aria-labelledby="otp-modal-title"
        aria-describedby="otp-modal-description"
      >
        <div
          style={{
            background: "#fff",
            padding: "2rem",
            borderRadius: "8px",
            width: "400px",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
          }}
        >
          <p id="otp-modal-title" className="text-2xl font-semibold mb-5">
            Verify OTP
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "3px",
            }}
          >
            {[...Array(4)].map((_, index) => (
              <input
                key={index}
                id={`otp-input-${index}`}
                type="text"
                maxLength="1"
                value={otp[index] || ""}
                onChange={(e) => handleOtpChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={index === 0 ? firstInputRef : null}
                className={`w-12 h-12 ${
                  otp ? "text-var(--heading-color)" : "text-red-600"
                } ${index !== 0 && index !== 5 ? "mx-2" : ""} border ${
                  otp ? "border-blue-300" : "border-red-500"
                } rounded text-center text-lg font-semibold focus:outline-none focus:ring-2 ${
                  otp ? "focus:ring-blue-500" : "focus:ring-red-500"
                }`}
              />
            ))}
          </div>

          <button
            className={`w-full text-white font-semibold py-2 rounded-lg mb-4 mt-5 ml-2 ${
              otpLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-700"
            } `}
            onClick={handleVerifyOtp}
            disabled={otpLoading}
          >
            {otpLoading ? "Verifying..." : "Verify"}
          </button>

          {otpError && <p className="text-red-600">{otpError}</p>}
        </div>
      </Modal>
      <Modal open={open} onClose={() => setOpen(false)}>
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="relative bg-white p-8 rounded-lg shadow-xl max-w-xl w-full flex flex-col items-center text-center"
          >
            {/* Modal Title & Message */}
            <h2 className="text-2xl font-bold text-gray-800">Success!</h2>
            <p className="text-gray-600 mt-2">Your submission was received.</p>

            {/* Floating Lottie Animation (overlapping the data box) */}
            <div className="absolute top-15 left-1/2 transform -translate-x-1/2 z-10">
              <Lottie
                animationData={animationData}
                loop={true}
                className="w-64 h-64 opacity-90"
              />
            </div>

            {/* Additional Data Section */}
            <div className="bg-gray-100 p-6 mt-8 rounded-lg w-full text-sm text-gray-700 relative z-0">
              <Lottie
                animationData={vector}
                loop={true}
                className="w-32 h-32 opacity-90 m-auto"
              />

              <p>
                <strong>Submission Date:</strong>{" "}
                {new Date().toLocaleDateString()}
              </p>
            </div>

            {/* Close Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg transition duration-300"
              onClick={() => setOpen(false)}
            >
              Close
            </motion.button>
          </motion.div>
        </div>
      </Modal>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;
